/*
 * Voyager Interiors - desktop header menu behaviour (Dawn 15.4.1 dropdown menu)
 *
 * Replaces the original mouseenter/blur script, which:
 *  - closed menus on blur before focus could reach the submenu (keyboard users
 *    could not reach any submenu link, focus fell back to the page body);
 *  - closed submenus instantly when the pointer left them (diagonal moves into
 *    the Dining Tables flyout closed it);
 *  - ran on touch screens 990px+ wide (iPad landscape, touch laptops), mixing
 *    emulated hover with tap-to-toggle;
 *  - let a click on a hover-opened menu label close the menu under the cursor.
 *
 * Behaviour now:
 *  - Mouse: menus open on hover and close CLOSE_DELAY ms after the pointer leaves.
 *    Moving onto another top-level item switches menus immediately.
 *  - Keyboard: native disclosure pattern. Tab moves between labels, Enter/Space
 *    opens, Tab walks into the open menu, Escape closes one level and returns
 *    focus to its label. Focus leaving a menu closes it.
 *  - Touch / pen: hover logic is ignored; tap-to-open (native <details>) applies,
 *    and tapping outside the menu closes it.
 * No changes to markup, styles or menu structure.
 */
(function () {
  'use strict';

  // Chosen from testing: the longest diagonal route from the "Dining Tables" label
  // to the bottom of its flyout spends up to ~174px (90th percentile ~129px) outside
  // the hover area. 450 ms covers that route at typical pointer speeds and keeps
  // menus from lingering noticeably after the pointer leaves.
  var CLOSE_DELAY = 450;

  var desktop = window.matchMedia('(min-width: 990px)');
  var nav = document.querySelector('.header__inline-menu');
  if (!nav) return;

  var allDetails = Array.prototype.slice.call(nav.querySelectorAll('details'));
  if (!allDetails.length) return;

  var timers = new WeakMap();
  var usingKeyboard = false;
  var lastPointerType = '';

  function summaryOf(details) {
    return details.querySelector(':scope > summary');
  }

  function isTopLevel(details) {
    return !details.parentElement.closest('details');
  }

  function cancelClose(details) {
    var t = timers.get(details);
    if (t) {
      clearTimeout(t);
      timers.delete(details);
    }
  }

  function setOpen(details, open) {
    if (open) {
      if (!details.hasAttribute('open')) details.setAttribute('open', '');
    } else {
      cancelClose(details);
      // Close any nested menus first so they do not reappear already open.
      details.querySelectorAll('details[open]').forEach(function (child) {
        setOpen(child, false);
      });
      if (details.hasAttribute('open')) details.removeAttribute('open');
    }
    var summary = summaryOf(details);
    if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function closeSiblings(details) {
    allDetails.forEach(function (other) {
      if (other === details || !other.hasAttribute('open')) return;
      if (other.contains(details)) return; // ancestor of the item being opened
      var sameLevel = isTopLevel(details)
        ? isTopLevel(other)
        : other.parentElement.closest('details') === details.parentElement.closest('details');
      if (sameLevel) setOpen(other, false);
    });
  }

  function scheduleClose(details) {
    cancelClose(details);
    timers.set(
      details,
      setTimeout(function () {
        timers.delete(details);
        // Keep a menu open while a keyboard user is working inside it.
        if (usingKeyboard && details.contains(document.activeElement)) return;
        setOpen(details, false);
      }, CLOSE_DELAY)
    );
  }

  function isMouse(event) {
    return event.pointerType === 'mouse' && desktop.matches;
  }

  // Track input modality so pointer and keyboard behaviour do not interfere.
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
        usingKeyboard = true;
      }
    },
    true
  );
  document.addEventListener(
    'pointerdown',
    function (event) {
      usingKeyboard = false;
      lastPointerType = event.pointerType;
      // Tap or click outside the menu closes any open menu (Dawn only closes on
      // focus loss, which touch browsers do not always trigger).
      if (!nav.contains(event.target)) {
        allDetails.forEach(function (details) {
          if (isTopLevel(details) && details.hasAttribute('open')) setOpen(details, false);
        });
      }
    },
    true
  );

  allDetails.forEach(function (details) {
    var summary = summaryOf(details);
    if (!summary) return;

    // Keep aria-expanded accurate however the menu was opened or closed, and make
    // sure only one menu per level is open (e.g. tapping Living then Dining).
    details.addEventListener('toggle', function () {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (details.open) {
        closeSiblings(details);
      } else {
        // A native toggle (tap, Enter) leaves nested menus open; reset them.
        details.querySelectorAll('details[open]').forEach(function (child) {
          setOpen(child, false);
        });
      }
    });

    // --- Mouse hover ---
    details.addEventListener('pointerenter', function (event) {
      if (!isMouse(event)) return;
      cancelClose(details);
      closeSiblings(details);
      setOpen(details, true);
    });

    details.addEventListener('pointerleave', function (event) {
      if (!isMouse(event)) return;
      if (details.hasAttribute('open')) scheduleClose(details);
    });

    // A mouse click on a label the pointer has already opened should not close
    // the menu under the cursor. Keyboard (detail === 0) and touch keep the
    // native toggle.
    summary.addEventListener('click', function (event) {
      if (event.detail === 0 || !desktop.matches) return;
      var type = event.pointerType || lastPointerType;
      if (type !== 'mouse') return;
      if (details.hasAttribute('open')) {
        event.preventDefault();
        setOpen(details, true);
      }
    });

    // --- Keyboard / focus ---
    // Close a menu once focus has moved outside it (relatedTarget is the element
    // receiving focus, so this never fires while focus moves into the submenu).
    details.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (next && details.contains(next)) return;
      if (!usingKeyboard && !next) return; // pointer clicks elsewhere are handled below
      setOpen(details, false);
    });

    // Escape closes one level at a time. Dawn's own handler (global.js) closes the
    // innermost open menu and focuses its label; stop the event there so the
    // parent menu stays open.
    if (!isTopLevel(details)) {
      details.addEventListener('keyup', function (event) {
        if (event.key === 'Escape') event.stopPropagation();
      });
    }
  });

  // Escape also closes a menu that was opened by hover without focus inside it.
  document.addEventListener('keyup', function (event) {
    if (event.key !== 'Escape' || nav.contains(document.activeElement)) return;
    allDetails.forEach(function (details) {
      if (isTopLevel(details) && details.hasAttribute('open')) setOpen(details, false);
    });
  });

  // Moving below the desktop breakpoint swaps to the drawer menu; close
  // anything left open in the inline menu.
  var onBreakpoint = function () {
    if (!desktop.matches) {
      allDetails.forEach(function (details) {
        setOpen(details, false);
      });
    }
  };
  if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
  else if (desktop.addListener) desktop.addListener(onBreakpoint);
})();
